import withLifecycles from 'async-lifecycles';
import styled from 'react-emotion';
import React, {Component, Fragment} from 'react';
import produce from 'immer';
import usersApi from '../../store/services/users';
import toastr from 'toastr';
import FormContainer from '../form-components/FormContainer';
import {Button, Form, Header, Icon, Input, Item, Label, Pagination, Popup, Table} from 'semantic-ui-react';
import {debounce} from 'lodash';
import {PortalWithState} from 'react-portal';
import searchImage from '../assets/search.svg';
import {Spring} from 'react-spring/renderprops';
import {ModalWrapper} from '../modal/ModalWrapper';
import Layout from '../layout/Layout';
import Page from '../layout/Page';
import LoadingContainer from '../layout/LoadingContainer';
import RequiresRole, {ROLES} from '../security/RequiresRole';
import {ErrorMessages} from '../form-components/ErrorMessages';
import {connect} from 'react-redux';
import Whitelist from '../../store/services/whitelist';
import WhitelistForm from './WhitelistForm';

const Styles = styled.div`
  .tableHeader {
    background-color: '#f00';
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    justify-content: space-between;

    .ui.header {
      margin-bottom: 0;
    }
  }
`;

class AllWhitelistsTable extends Component {
  state = {
    loading: false, pageInfo: {}, whitelist: undefined, orderByName: false, activePage: 1, searchTerm: '',
  };

  _mutate = cb => {
    this.setState(produce(cb));
  };

  componentDidMount() {
    this._loadWhitelists(0, '');
  }

  _deleteWhitelist = async whitelist => {
    try {
      await Whitelist.unwhitelist(whitelist.id);
      if (whitelist.userId) {
        let user = await usersApi.fetchById(whitelist.userId);
        user.result.isWhitelisted = false;
        await usersApi.update(user.result);
      }
      toastr.info('Whitelist removed');
      this._loadWhitelists(0, '');
      this.setState({searchTerm: ''});
      this._closePortal();
    } catch (error) {
      toastr.warning('Error removing whitelist');
    }
  };

  _loadWhitelists = (pageNumber, searchTerm) => {
    withLifecycles(() => Whitelist.fetch({
      request: {
        page: pageNumber, pageSize: 20,
      }, searchTerm: searchTerm, orderByName: this.state.orderByName,
    }), ({loading, data, error}) => {
      this.setState({activePage: pageNumber + 1});
      this.setState(produce(draft => {
        draft.loading = loading;

        if (data && !data.hasErrors) {
          draft.pageInfo = data.result;
        }
      }));
    });
  };

  _save = () => {
    withLifecycles(() => Whitelist.update(this.state.whitelist), ({loading, data, error}) => {
      this.setState(produce(draft => {
        draft.saving = loading;
        draft.error = error;

        if (data && !data.hasErrors) {
          this.props.history.push('/manage/whitelists');
          toastr.success('Whitelist Updated');
          this._loadWhitelists(0, '');
          this.setState({searchTerm: ''});
          this._closePortal();
        }
      }));
    });
  };

  _showDetail = whitelist => {
    this._openPortal();
    this.setState({whitelist, error: {}});
  };

  _handleTextChanged = (event, {name, value}) => {
    this._mutate(draft => {
      draft.whitelist[name] = value;
    });
  };

  render() {
    const {pageInfo, activePage} = this.state;
    const whitelists = pageInfo.data || [];
    return (<FormContainer loading={this.state.loading}>
      <Styles>
        <div className="tableHeader">
          <Header as="h1">Whitelists</Header>
          <div>
            <Input
              placeholder="Search Whitelist..."
              onChange={debounce((e, searchTerm) => {
                this._loadWhitelists(0, searchTerm.value);
                this.setState({searchTerm: searchTerm.value});
              }, 400)}
            />
          </div>
        </div>
      </Styles>
      {pageInfo.pageCount > 0 && (<PortalWithState closeOnEsc>
        {({openPortal, closePortal, portal}) => {
          this._closePortal = closePortal;
          this._openPortal = openPortal;
          return (<Fragment>
            <Table basic="very" selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell />

                  <Table.HeaderCell>Customer</Table.HeaderCell>
                  <Table.HeaderCell>First Name</Table.HeaderCell>
                  <Table.HeaderCell>Last Name</Table.HeaderCell>
                  <Table.HeaderCell>Email Address</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {whitelists.map(whitelists => this._renderRow(whitelists))}
              </Table.Body>
            </Table>
            <Pagination
              activePage={activePage}
              onPageChange={async (e, paginationInfo) => {
                await this._loadWhitelists(paginationInfo.activePage - 1, this.state.searchTerm);
              }}
              ellipsisItem={{
                content: <Icon name="ellipsis horizontal" />, icon: true,
              }}
              firstItem={{
                content: <Icon name="angle double left" />, icon: true,
              }}
              lastItem={{
                content: <Icon name="angle double right" />, icon: true,
              }}
              prevItem={{content: <Icon name="angle left" />, icon: true}}
              nextItem={{
                content: <Icon name="angle right" />, icon: true,
              }}
              totalPages={pageInfo.pageCount}
            />
            {portal(this._renderDetailModal())}
          </Fragment>);
        }}
      </PortalWithState>)}
      {whitelists.length === 0 && (<Item.Group>
        <Item>
          <Item.Image size="mini" src={searchImage} />
          <Item.Content verticalAlign="middle">
            <strong>No Whitelists.</strong>
            <div>
              <small>
                No whitelists match your search results. Please clear or
                refine your search.
              </small>
            </div>
          </Item.Content>
        </Item>
      </Item.Group>)}
    </FormContainer>);
  }

  _renderRow = whitelist => (<Table.Row key={whitelist.id}>
    <Table.Cell collapsing>
      <Button
        className="clear"
        icon
        onClick={() => this._showDetail(whitelist)}
      >
        <Icon name="edit" />
      </Button>
    </Table.Cell>
    <RequiresRole roles={[ROLES.SYSTEM_ADMIN, ROLES.MM_ADMIN]}>
      <Table.Cell>
        {whitelist.customer && (<div>
          {whitelist.customer}
          <Label
            size="mini"
            style={{
              backgroundColor: '#e8c164', color: '#FFF', marginLeft: '5px',
            }}
          >
            Customer
          </Label>
        </div>)}
      </Table.Cell>
    </RequiresRole>
    <Table.Cell>{whitelist.firstName}</Table.Cell>
    <Table.Cell>{whitelist.lastName}</Table.Cell>
    <Table.Cell>{whitelist.emailAddress}</Table.Cell>
  </Table.Row>);

  _renderDetailModal = () => {
    const {whitelist, error = {}} = this.state;

    return (<Spring from={{right: '-100%'}} to={{right: '0%'}}>
      {styles => (<ModalWrapper style={styles}>
        <Layout className="page-wrapper">
          <Header className="page-header">
            <Page.HeaderWithActions title="Whitelisted User Info">
              <Button
                icon="close"
                size="mini"
                onClick={this._closePortal}
              />
            </Page.HeaderWithActions>
          </Header>
          <Layout.Fill scroll padded>
            <LoadingContainer hideContentWhenLoading>
              <Form onSubmit={this._save}>
                {whitelist.userId === null && (<WhitelistForm
                  whitelist={whitelist}
                  handleTextChanged={this._handleTextChanged}
                  isReadOnly={false}
                  isBeingEdited={true}
                />)}
                {whitelist.userId === null && (<Button type="Submit" primary>
                  Update
                </Button>)}
                {whitelist.userId !== null && (<WhitelistForm
                  whitelist={whitelist}
                  handleTextChanged={this._handleTextChanged}
                  isReadOnly={true}
                  isBeingEdited={true}
                />)}

                <ErrorMessages error={error} />

                <Button basic onClick={this._closePortal}>
                  Cancel
                </Button>

                <Popup
                  trigger={<Button basic icon floated="right" type="button">
                    Remove from Whitelist
                  </Button>}
                  content={<Button
                    type="button"
                    color="red"
                    content="Are you sure?"
                    onClick={() => this._deleteWhitelist(whitelist)}
                  />}
                  on="click"
                  position="left center"
                />
              </Form>
            </LoadingContainer>
          </Layout.Fill>
        </Layout>
      </ModalWrapper>)}
    </Spring>);
  };
}

const mapStateToProps = ({customers, identity}) => ({
  customers: customers.data, role: identity.user.role,
});

export default connect(mapStateToProps)(AllWhitelistsTable);
